import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useMemo, useRef, useCallback, useState } from 'react';
import useCopilot from "./hooks/useCopilot";

/**
 * Context for Copilot feature registration and management
 * @type {React.Context}
 */
const CopilotContext = /*#__PURE__*/createContext();
const initialMethods = {};

/**
 * Provider component for Copilot features
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
const CopilotProvider = _ref => {
  let {
      children
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const featureMethods = useRef(initialMethods);
  const copilotState = useCopilot();

  /**
   * Register methods for a specific feature type
   * @param {string} featureType - Type of feature (e.g., 'forceBI', 'analytics')
   * @param {Object} methods - Methods to register for this feature
   */
  const registerFeatureMethods = useCallback(function (featureType, methods) {
    if (methods === void 0) {
      methods = {};
    }
    featureMethods.current[featureType] = _objectSpread(_objectSpread({}, featureMethods.current[featureType] || {}), methods);
  }, []);
  const getMethodsForCopilot = useMemo(() => ({
    registerFeatureMethods
  }), [registerFeatureMethods]);
  return /*#__PURE__*/React.createElement(CopilotContext.Provider, {
    value: _objectSpread(_objectSpread({}, copilotState), {}, {
      getMethodsForCopilot,
      featureMethods: featureMethods.current
    }, props)
  }, children);
};
const CopilotConsumer = CopilotContext.Consumer;
export { CopilotContext, CopilotProvider, CopilotConsumer };

/**
 * Example usage:
 *
 * import React, { useContext, useEffect } from 'react';
 * import { CopilotContext } from './path/to/Context';
 *
 * const MyFeature = () => {
 *   const { getMethodsForCopilot } = useContext(CopilotContext);
 *
 *   useEffect(() => {
 *     // Register methods for a specific feature type
 *     getMethodsForCopilot.registerFeatureMethods('analytics', {
 *       trackEvent: (eventName, data) => {
 *         console.log(`Tracking ${eventName}:`, data);
 *         // Actual implementation here
 *       },
 *       pageView: (pageName) => {
 *         console.log(`Page view: ${pageName}`);
 *         // Actual implementation here
 *       }
 *     });
 *
 *     // Register methods for another feature type
 *     getMethodsForCopilot.registerFeatureMethods('forceBI', {
 *       sendData: (payload) => {
 *         // Implementation
 *       }
 *     });
 *   }, [getMethodsForCopilot]);
 *
 *   return <div>My Feature</div>;
 * };
 *
 * // To use the registered methods elsewhere:
 * const ConsumingComponent = () => {
 *   const { featureMethods } = useContext(CopilotContext);
 *
 *   const handleClick = () => {
 *     // Access and use registered methods
 *     if (featureMethods.analytics?.trackEvent) {
 *       featureMethods.analytics.trackEvent('button_click', { id: 'submit-btn' });
 *     }
 *   };
 *
 *   return <button onClick={handleClick}>Click Me</button>;
 * };
 */